/** Style tokens: a small allowlist in normalized units, with a safe default for anything else. */

import type { Box } from "./scene/sceneTree";

export interface Screen {
  readonly width: number;
  readonly height: number;
}

export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export type StyleTokens = Readonly<Record<string, string>>;

const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

const ALIGNMENTS = ["left", "center", "right"] as const;

export type Alignment = (typeof ALIGNMENTS)[number];

/** A colour token, accepted only as hex so an unparseable value cannot reach the renderer. */
export function colorToken(style: StyleTokens, token: string, fallback: string): string {
  const value = style[token];
  return value !== undefined && HEX.test(value) ? value : fallback;
}

export function numberToken(style: StyleTokens, token: string, fallback: number): number {
  const value = style[token];
  if (value === undefined) return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function alignmentToken(style: StyleTokens): Alignment {
  const value = style.align;
  return ALIGNMENTS.find((alignment) => alignment === value) ?? "left";
}

/** The anchor a text uses for its alignment, so the box edge is what the author sees. */
export function anchorFor(alignment: Alignment): number {
  if (alignment === "center") return 0.5;
  return alignment === "right" ? 1 : 0;
}

export function pixels(box: Box, screen: Screen): Rect {
  return {
    x: box.left * screen.width,
    y: box.top * screen.height,
    width: box.width * screen.width,
    height: box.height * screen.height,
  };
}

/** Font size is a fraction of canvas height, so both targets read the same at any size. */
export function fontSize(style: StyleTokens, screen: Screen): number {
  return Math.max(1, numberToken(style, "font_size", 0.04) * screen.height);
}

export function opacity(style: StyleTokens): number {
  return Math.min(1, Math.max(0, numberToken(style, "opacity", 1)));
}
